import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { Receipt, FileText, Link2, Calculator, BarChart3, Shield, CheckCircle, Lock, Cloud } from "lucide-react";

export default function LandingPage() {
  const { user, login, isConfigured, loading } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/dashboard");
    return null;
  }

  const features = [
    {
      icon: Receipt,
      title: "Receipt Scanning & OCR",
      description: "Upload receipts and automatically extract merchant, date, amounts, and VAT using AI-powered OCR."
    },
    {
      icon: Cloud,
      title: "Your Data, Your Drive",
      description: "All data stored exclusively in your Google Drive. No central database. Complete data ownership."
    },
    {
      icon: Link2,
      title: "Smart Matching",
      description: "Automatically match receipts to bank transactions by date and amount. Manual linking supported."
    },
    {
      icon: Calculator,
      title: "VAT Calculation",
      description: "Automatic VAT calculation with support for 20%, 5%, 0%, and exempt rates. Monthly summaries."
    },
    {
      icon: BarChart3,
      title: "Monthly Reports",
      description: "Generate accountant-ready PDF reports with transaction summaries and VAT breakdowns."
    },
    {
      icon: Lock,
      title: "Privacy First",
      description: "GDPR-friendly and HMRC audit-ready. Your data never touches our servers."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Accruey
            </span>
          </div>
          {isConfigured ? (
            <Button
              data-testid="header-login-btn"
              onClick={login}
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {loading ? "Loading..." : "Sign in with Google"}
            </Button>
          ) : (
            <span className="text-sm text-amber-600">OAuth not configured</span>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                100% Privacy-First • Your Data in Your Drive
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Receipt Management
                <span className="block text-blue-600">Without the Database</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Manage receipts, calculate VAT, and generate accountant-ready reports. All data stored exclusively in your personal Google Drive—no central database, complete data ownership.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {isConfigured ? (
                  <Button
                    data-testid="hero-get-started-btn"
                    onClick={login}
                    disabled={loading}
                    size="lg"
                    className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg"
                  >
                    {loading ? "Loading..." : "Get Started with Google"}
                  </Button>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                    <strong>Setup Required:</strong> Add <code className="bg-amber-100 px-1 rounded">REACT_APP_GOOGLE_CLIENT_ID</code> to enable Google Sign-In.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>No database</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>GDPR compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>HMRC ready</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80"
                alt="Financial documents and receipts"
                className="rounded-2xl shadow-2xl w-full object-cover"
                style={{ maxHeight: '500px' }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Cloud className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Stored in</p>
                    <p className="text-lg font-bold text-slate-900">Your Google Drive</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything You Need
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive tools to manage your business finances with complete privacy.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-12 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How It Works
          </h2>
          <div className="space-y-8">
            {[
              { step: 1, title: "Sign in with Google", desc: "Authenticate securely with your Google account. We request only minimal permissions." },
              { step: 2, title: "Folder Created in Your Drive", desc: "Accruey creates a structured folder in your Google Drive to store all your data." },
              { step: 3, title: "Upload & Organize", desc: "Upload receipts, import bank statements. Everything is saved directly to your Drive." },
              { step: 4, title: "Generate Reports", desc: "Create PDFs and exports. Download or share with your accountant directly from Drive." }
            ].map((item) => (
              <div key={item.step} className="flex gap-6 items-start">
                <div className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Take Control?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Your receipts. Your data. Your Google Drive.
          </p>
          {isConfigured && (
            <Button
              data-testid="cta-get-started-btn"
              onClick={login}
              disabled={loading}
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
            >
              {loading ? "Loading..." : "Start Free Today"}
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <Receipt className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Accruey</span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 Accruey. Privacy-first receipt management.
          </p>
        </div>
      </footer>
    </div>
  );
}
